'use client';
import React, { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import { DateTime } from 'luxon';
import type { ColumnDef } from '@benzinga/table';
import type { AsOf, Financial, Financials, OperationRatio } from '@benzinga/securities-manager';
import { SecuritiesManager } from '@benzinga/securities-manager';
import { ChartManager } from '@benzinga/chart-manager';
import { SessionContext } from '@benzinga/session-context';
import { useQuoteTrackClick } from '../../utils/quote/useQuoteTracking';
import QuoteListTable from './QuoteListTable';
import { ChartPlotSkeleton, PeerComparisonSkeleton } from './QuoteSkeletons';
import { formatMoneyShort, formatRatioAsPercent } from './formatters';
import { deriveMetrics, type PeerMetrics } from './peerComparison';
const ReactECharts = React.lazy(() => import('echarts-for-react'));

const formatNumber = (value: number | undefined | null, decimals = 2): string => {
  if (value == null || isNaN(value)) return '—';
  return value.toFixed(decimals);
};

type SortKey = keyof Omit<PeerMetrics, 'symbol'>;

type RangeOption = '1m' | '3m' | '1y';

const RANGE_LABELS: Record<RangeOption, string> = { '1m': '1M', '3m': '3M', '1y': '1Y' };

const SERIES_COLORS = [
  '#2F80ED',
  '#30BF60',
  '#F2994A',
  '#9B59B6',
  '#E74C3C',
  '#1ABC9C',
  '#F39C12',
  '#3498DB',
  '#E91E63',
  '#607D8B',
];

const RelativePerformanceChart: React.FC<{
  symbol: string;
  peerSymbols: string[];
}> = ({ symbol, peerSymbols }) => {
  const session = React.useContext(SessionContext);
  const chartManager = React.useMemo(() => session.getManager(ChartManager), [session]);
  const trackClick = useQuoteTrackClick();

  const [range, setRange] = React.useState<RangeOption>('1m');
  const [seriesData, setSeriesData] = React.useState<Record<string, [string, number][]>>({});
  const [loading, setLoading] = React.useState(false);

  const allSymbols = useMemo(() => [symbol, ...peerSymbols.slice(0, 5)], [symbol, peerSymbols]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSeriesData({});

    async function fetchAll() {
      const results = await Promise.all(
        allSymbols.map(async chartSymbol => {
          const res = await chartManager.getChart({ from: range, interval: '1d', symbol: chartSymbol });
          return { chartSymbol, candles: res?.ok?.candles ?? [] };
        }),
      );

      if (cancelled) return;

      const next: Record<string, [string, number][]> = {};
      for (const { chartSymbol, candles } of results) {
        if (!candles.length) continue;
        const base = Number(candles[0].close);
        if (!base) continue;
        next[chartSymbol] = candles.map(candle => [
          candle.dateTime ?? String(candle.time),
          Math.round((Number(candle.close) / base - 1) * 10000) / 100,
        ]);
      }
      setSeriesData(next);
      setLoading(false);
    }

    fetchAll().catch(() => {
      if (!cancelled) {
        setSeriesData({});
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [range, chartManager, allSymbols]);

  const chartOpt = useMemo((): EChartsOption => {
    const seriesSymbols = Object.keys(seriesData);
    if (!seriesSymbols.length) return {} as EChartsOption;
    return {
      grid: { left: 52, right: 16, top: 8, bottom: 56 },
      legend: { data: seriesSymbols, bottom: 0, textStyle: { fontSize: 10 } },
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const rows = Array.isArray(params) ? params : [params];
          return rows
            .map(rowParam => {
              const row = rowParam as { seriesName: string; value: [string, number] };
              return `${row.seriesName}: ${row.value[1].toFixed(2)}%`;
            })
            .join('<br/>');
        },
      },
      xAxis: { type: 'time', axisLabel: { fontSize: 9 } },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (value: number) => `${value}%`, fontSize: 9 },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      series: seriesSymbols.map((seriesSymbol, index) => ({
        name: seriesSymbol,
        type: 'line',
        data: seriesData[seriesSymbol],
        symbol: 'none',
        lineStyle: {
          color: SERIES_COLORS[index % SERIES_COLORS.length],
          width: seriesSymbol === symbol ? 2.5 : 1.5,
        },
        itemStyle: { color: SERIES_COLORS[index % SERIES_COLORS.length] },
      })),
    } as EChartsOption;
  }, [seriesData, symbol]);

  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Relative Performance</p>
        <div className="flex gap-1">
          {(Object.keys(RANGE_LABELS) as RangeOption[]).map(rangeOption => (
            <button
              key={rangeOption}
              onClick={() => {
                setRange(rangeOption);
                trackClick('peers_range_toggle');
              }}
              className={`rounded px-2 py-0.5 text-xs font-semibold ${rangeOption === range ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
            >
              {RANGE_LABELS[rangeOption]}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <ChartPlotSkeleton label="Loading relative performance" />
      ) : Object.keys(seriesData).length === 0 ? (
        <div className="flex h-[220px] items-center justify-center text-sm text-gray-400">No chart data available.</div>
      ) : (
        <React.Suspense fallback={<ChartPlotSkeleton />}>
          <ReactECharts option={chartOpt} style={{ height: 220 }} />
        </React.Suspense>
      )}
    </div>
  );
};

const peerCellClass =
  (currentSymbol: string) =>
  ({ data }: { data: PeerMetrics }) =>
    `${data.symbol === currentSymbol ? 'font-semibold text-gray-900' : 'text-gray-700'}`;

const peerMetricColumn = (
  field: SortKey,
  headerName: string,
  format: (metric: PeerMetrics) => string,
  currentSymbol: string,
): ColumnDef<PeerMetrics> => ({
  cellClass: ({ data }) => `text-right ${peerCellClass(currentSymbol)({ data })}`,
  field,
  headerClass: 'text-right',
  headerName,
  sortEnabled: true,
  valueFormatter: ({ data }) => format(data),
});

interface PeerComparisonSectionProps {
  symbol: string;
  peerSymbols: string[];
}

const PeerComparisonSection: React.FC<PeerComparisonSectionProps> = ({ symbol, peerSymbols }) => {
  const [peers, setPeers] = React.useState<PeerMetrics[]>([]);
  const [loaded, setLoaded] = React.useState(false);

  const session = React.useContext(SessionContext);
  const securitiesManager = React.useMemo(() => session.getManager(SecuritiesManager), [session]);
  const peerSymbolsKey = peerSymbols.join(',');
  const hasRevenueGrowth = useMemo(() => peers.some(peer => peer.revenueGrowth != null), [peers]);

  const peerColumnsDef = useMemo((): ColumnDef<PeerMetrics>[] => {
    const columns: ColumnDef<PeerMetrics>[] = [
      {
        cellRenderer: ({ data }) =>
          data.symbol === symbol ? (
            <span className="font-bold text-bzblue-1000" aria-current="page">
              {data.symbol}
            </span>
          ) : (
            <a
              href={`/quote/${data.symbol}`}
              className="text-bzblue-1000 hover:underline focus:underline focus:outline-none"
              aria-label={`View ${data.symbol} quote page`}
            >
              {data.symbol}
            </a>
          ),
        field: 'symbol',
        headerName: 'Ticker',
      },
      peerMetricColumn('marketCap', 'Mkt Cap', metric => formatMoneyShort(metric.marketCap), symbol),
      peerMetricColumn('pe', 'P/E', metric => formatNumber(metric.pe), symbol),
      peerMetricColumn('pb', 'P/B', metric => formatNumber(metric.pb), symbol),
      peerMetricColumn('eps', 'EPS', metric => formatNumber(metric.eps), symbol),
      peerMetricColumn('roe', 'ROE %', metric => formatRatioAsPercent(metric.roe), symbol),
      peerMetricColumn('de', 'D/E', metric => formatNumber(metric.de), symbol),
      peerMetricColumn('grossMargin', 'Gross Margin', metric => formatRatioAsPercent(metric.grossMargin), symbol),
      peerMetricColumn('netMargin', 'Net Margin', metric => formatRatioAsPercent(metric.netMargin), symbol),
    ];
    if (hasRevenueGrowth) {
      columns.push(
        peerMetricColumn('revenueGrowth', 'Rev Growth', metric => formatRatioAsPercent(metric.revenueGrowth), symbol),
      );
    }
    return columns;
  }, [symbol, hasRevenueGrowth]);

  React.useEffect(() => {
    const allSymbols = [symbol, ...peerSymbols.slice(0, 9)];
    const csvSymbols = allSymbols.join(',');
    const revenueHistoryAsOf = DateTime.local().minus({ months: 24 }).toFormat('yyyy-MM-dd') as AsOf;

    async function load() {
      try {
        const [fundResult, opResult] = await Promise.all([
          securitiesManager.getFundamentals({ period: '12M', symbols: csvSymbols }),
          securitiesManager
            .getOperationRatios({ period: '12M', asOf: 'LATEST_REPORTED', symbols: csvSymbols })
            .catch(() => ({ ok: [] as Financials[] })),
        ]);
        if (!fundResult.ok) return;

        const opBySymbol = new Map<string, OperationRatio[]>();
        for (const row of opResult.ok ?? []) {
          const rowSymbol = String(row.symbol ?? row.id ?? '');
          if (rowSymbol && Array.isArray(row.operationRatios) && row.operationRatios.length) {
            opBySymbol.set(rowSymbol, row.operationRatios);
          }
        }

        const merged = fundResult.ok.map(fundamentals => {
          const fallback = opBySymbol.get(String(fundamentals.symbol ?? fundamentals.id ?? ''));
          return fallback && (!fundamentals.operationRatios || fundamentals.operationRatios.length === 0)
            ? { ...fundamentals, operationRatios: fallback }
            : fundamentals;
        });

        const missingGrowth = merged
          .filter(fundamentals => fundamentals.operationRatios?.[0]?.revenueGrowth == null)
          .map(fundamentals => String(fundamentals.symbol ?? fundamentals.id ?? ''))
          .filter(Boolean);
        const historyBySymbol = new Map<string, Financial[]>();
        if (missingGrowth.length) {
          const historyResult = await securitiesManager
            .getFinancials({ period: '12M', asOf: revenueHistoryAsOf, symbols: missingGrowth.join(',') })
            .catch(() => ({ ok: [] as Financials[] }));
          for (const row of historyResult.ok ?? []) {
            const rowSymbol = String(row.symbol ?? row.id ?? '');
            if (rowSymbol && Array.isArray(row.financials)) {
              historyBySymbol.set(rowSymbol, row.financials);
            }
          }
        }

        const mapped = merged.map(fundamentals =>
          deriveMetrics(fundamentals, historyBySymbol.get(String(fundamentals.symbol ?? fundamentals.id ?? ''))),
        );

        setPeers(mapped);
      } finally {
        setLoaded(true);
      }
    }

    load().catch(() => setLoaded(true));
  }, [securitiesManager, symbol, peerSymbolsKey, peerSymbols]);

  const chartSorted = useMemo(() => {
    return [...peers].sort((left, right) => {
      const leftPe = left.pe ?? Infinity;
      const rightPe = right.pe ?? Infinity;
      return leftPe - rightPe;
    });
  }, [peers]);

  const barChartOpt = useMemo((): EChartsOption => {
    if (!chartSorted.length) return {} as EChartsOption;
    const labels = chartSorted.map(peer => peer.symbol);

    const makeBar = (data: (number | null)[], name: string, color: string) => ({
      name,
      type: 'bar' as const,
      data: data.map(value => (value != null ? Math.round(value * 1000) / 10 : null)),
      itemStyle: { color },
      tooltip: { valueFormatter: (value: number) => `${value?.toFixed(1)}%` },
    });

    return {
      grid: { left: 52, right: 16, top: 8, bottom: 56 },
      legend: {
        data: hasRevenueGrowth ? ['Gross Margin', 'Net Margin', 'Rev Growth'] : ['Gross Margin', 'Net Margin'],
        bottom: 0,
        textStyle: { fontSize: 10 },
      },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10 } },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (value: number) => `${value}%`, fontSize: 10 },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      series: [
        makeBar(
          chartSorted.map(peer => peer.grossMargin),
          'Gross Margin',
          '#30BF60',
        ),
        makeBar(
          chartSorted.map(peer => peer.netMargin),
          'Net Margin',
          '#2f80ed',
        ),
        ...(hasRevenueGrowth
          ? [
              makeBar(
                chartSorted.map(peer => peer.revenueGrowth),
                'Rev Growth',
                '#f2994a',
              ),
            ]
          : []),
      ],
    } as EChartsOption;
  }, [chartSorted, hasRevenueGrowth]);

  const valuationChartOpt = useMemo((): EChartsOption => {
    if (!chartSorted.length) return {} as EChartsOption;
    const labels = chartSorted.map(peer => peer.symbol);

    return {
      grid: { left: 48, right: 16, top: 8, bottom: 56 },
      legend: { data: ['P/E', 'P/B'], bottom: 0, textStyle: { fontSize: 10 } },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10 } },
      yAxis: { type: 'value', axisLabel: { fontSize: 10 }, splitLine: { lineStyle: { color: '#f0f0f0' } } },
      series: [
        {
          name: 'P/E',
          type: 'bar',
          data: chartSorted.map(peer => peer.pe),
          itemStyle: { color: '#4B8BF4' },
          tooltip: { valueFormatter: (value: number | null) => (value != null ? value.toFixed(1) : '—') },
        },
        {
          name: 'P/B',
          type: 'bar',
          data: chartSorted.map(peer => peer.pb),
          itemStyle: { color: '#9B59B6' },
          tooltip: { valueFormatter: (value: number | null) => (value != null ? value.toFixed(1) : '—') },
        },
      ],
    } as EChartsOption;
  }, [chartSorted]);

  if (!loaded) {
    return <PeerComparisonSkeleton />;
  }
  if (peers.length === 0) {
    return <div className="py-8 text-center text-sm text-gray-400">No peer comparison data available.</div>;
  }

  return (
    <div className="flex flex-col gap-y-8">
      <RelativePerformanceChart symbol={symbol} peerSymbols={peerSymbols} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded border border-[#e9ecef] bg-white p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Valuation Multiples</p>
          <React.Suspense fallback={<ChartPlotSkeleton label="Loading valuation chart" />}>
            <ReactECharts option={valuationChartOpt} style={{ height: 220 }} />
          </React.Suspense>
        </div>
        <div className="rounded border border-[#e9ecef] bg-white p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Margins &amp; Growth</p>
          <React.Suspense fallback={<ChartPlotSkeleton label="Loading margins chart" />}>
            <ReactECharts option={barChartOpt} style={{ height: 220 }} />
          </React.Suspense>
        </div>
      </div>

      <QuoteListTable columnsDef={peerColumnsDef} rowData={peers} sortEnabled />
    </div>
  );
};

export default PeerComparisonSection;
